import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';
import { QUOTE_TABS, TIME_RANGES, type HistoryKind, type QuoteTab, type TimeRange } from '@worki/shared';
import { AnchoredMenu } from '../AnchoredMenu.js';
import { cn } from '../../lib/cn.js';

interface HistoryFiltersProps {
  kind: HistoryKind | 'all';
  tab: QuoteTab;
  range: TimeRange;
  counts: Partial<Record<QuoteTab, number>> | undefined;
  onKindChange: (kind: HistoryKind | 'all') => void;
  onStatusChange: (tab: QuoteTab) => void;
  onRangeChange: (range: TimeRange) => void;
}

/** All three filters stay visible together; choosing one never opens another menu. */
export function HistoryFilters({ kind, tab, range, counts, onKindChange, onStatusChange, onRangeChange }: HistoryFiltersProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState(320);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const folder = kind === 'invoice-folder' || kind === 'library-folder';
  const close = () => { setOpen(false); trigger.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    const resize = () => setWidth(Math.min(360, window.innerWidth - 32));
    resize();
    panel.current?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]')?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', resize);
    return () => {
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', resize);
    };
  }, [open]);

  const optionClass = (selected: boolean) => cn(
    'min-h-10 rounded-lg border px-3 py-2 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40',
    selected ? 'border-primary/50 bg-primary/10 text-primary font-medium' : 'border-border bg-card hover:bg-muted',
  );

  return (
    <div className="min-w-0 flex-1">
      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        className="flex min-h-12 w-full items-center gap-2 rounded-xl bg-muted px-3 py-2 text-start hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm">
            <span className="font-medium text-primary">{t(`history.kind.${kind}`)}</span>
            <span aria-hidden="true"> · </span>
            <span>{t(`quotes.tab.${tab}`)}</span>
          </span>
          <span className="block text-xs text-muted-foreground">{t('quotes.rangeLabel')}: {t(`dashboard.range.${range}`)}</span>
        </span>
        <ChevronDown aria-hidden="true" className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={trigger} align="start" width={width} maxHeight={480}>
        <div ref={panel} id={id} role="dialog" aria-label={`${t('history.kindLabel')} · ${t('inquiries.status')} · ${t('quotes.rangeLabel')}`} className="p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground">{t('history.kindLabel')}</span>
            <button type="button" onClick={close} aria-label={t('common.close')} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <fieldset>
            <legend className="sr-only">{t('history.kindLabel')}</legend>
            <div className="flex flex-wrap gap-1.5">
              {(['all', 'quote', 'signature', 'invoice-folder'] as const).map(option => (
                <button key={option} type="button" aria-pressed={kind === option} onClick={() => onKindChange(option)} className={optionClass(kind === option)}>
                  {t(`history.kind.${option}`)}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset className="border-t border-border mt-3 pt-3">
            <legend className="float-start w-full mb-2 text-xs font-medium text-muted-foreground">{t('inquiries.status')}</legend>
            <div className="clear-both flex flex-wrap gap-1.5">
              {QUOTE_TABS.map(option => (
                <button key={option} type="button" aria-pressed={tab === option} disabled={folder && option !== 'all'} onClick={() => onStatusChange(option)} className={optionClass(tab === option)}>
                  {t(`quotes.tab.${option}`)} <span className="ms-1 text-muted-foreground ltr-nums">{counts?.[option] ?? 0}</span>
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset className="border-t border-border mt-3 pt-3">
            <legend className="float-start w-full mb-2 text-xs font-medium text-muted-foreground">{t('quotes.rangeLabel')}</legend>
            <div className="clear-both flex flex-wrap gap-1.5">
              {TIME_RANGES.map(option => (
                <button key={option} type="button" aria-pressed={range === option} onClick={() => onRangeChange(option)} className={optionClass(range === option)}>
                  {t(`dashboard.range.${option}`)}
                </button>
              ))}
            </div>
          </fieldset>
        </div>
      </AnchoredMenu>
    </div>
  );
}
